// Arrival Desk — no-show / cancellation risk for the next days with one-tap re-confirmation e-mail.
import React, { useEffect, useState } from 'react';
import { ShieldAlert, Mail, Phone, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

export const LEVEL_CLS = { high: 'bg-rose-100 text-rose-800 border-rose-200', medium: 'bg-amber-100 text-amber-900 border-amber-200', low: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
const FACTOR = { ota_channel: 'OTA booking', direct_booking: 'direct booking', not_confirmed: 'not confirmed', booked_long_ago: 'booked >90 days ago', last_minute: 'last minute', deposit_paid: 'deposit paid', nothing_paid: 'nothing paid yet', online_checkin_done: 'online check-in done', mail_not_opened: 'pre-arrival mail not opened', mail_clicked: 'opened pre-arrival mail', no_contact: 'no e-mail / phone', one_night: '1 night', past_cancellation: 'cancelled before', returning_guest: 'returning guest' };

export const RiskBadge = ({ r }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase ${LEVEL_CLS[r.level]}`} title={r.factors.map((f) => FACTOR[f] || f).join(', ')} data-testid={`risk-badge-${r.booking_id}`}>{r.level} {r.score}</span>
);

const RiskBoard = ({ date, canEdit, onRisks }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const d = await apiCall(`/api/admin/arrivals/risk?days=${days}&date_from=${date}`);
      setData(d);
      onRisks && onRisks(Object.fromEntries(d.arrivals.map((a) => [a.booking_id, a])));
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date, days]);

  const reconfirm = async (a) => {
    setMsg('');
    try {
      const r = await apiCall(`/api/admin/arrivals/risk/${a.booking_id}/reconfirm`, { method: 'POST' });
      setMsg(`Re-confirmation ${r.channel === 'whatsapp' ? 'WhatsApp message' : 'e-mail'} (${r.lang}) sent to ${r.to}.`);
      await load();
    } catch (e) { setMsg(e.message); }
  };

  if (!data) return null;
  const risky = data.arrivals.filter((a) => a.level !== 'low');
  return (
    <div className="bg-white border rounded-xl" data-testid="risk-board">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-left" data-testid="risk-board-toggle">
        <span className="font-semibold text-slate-900 inline-flex items-center gap-2 text-sm"><ShieldAlert className="w-4 h-4 text-orange-600" /> No-show / cancellation risk · next {days} days</span>
        <span className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-semibold" data-testid="risk-high-count">{data.high} high</span>
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 font-semibold" data-testid="risk-medium-count">{data.medium} medium</span>
          <span className="text-slate-500">of {data.count} arrivals</span>
          <span className="text-slate-400">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border rounded px-2 py-1" data-testid="risk-days">{[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>next {d} day{d > 1 ? 's' : ''}</option>)}</select>
            <button type="button" onClick={load} disabled={busy} className="inline-flex items-center gap-1 border rounded px-2 py-1 hover:bg-slate-50" data-testid="risk-refresh"><RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh</button>
            <span className="text-slate-500">Score 0–100 from channel, lead time, payment, online check-in, mail engagement, contact data and history. ≥60 high · ≥35 medium.</span>
          </div>
          {!data.contact_known && data.count > 0 && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="risk-contact-hint">Beds24 returns no guest e-mails/phones (token without the <b>bookings-personal</b> scope) — re-confirmation e-mails only work for guests who left an e-mail at online check-in.</p>}
          {msg && <p className="text-xs text-slate-700" data-testid="risk-msg">{msg}</p>}
          {risky.length === 0 && <p className="text-xs text-emerald-700" data-testid="risk-none">{data.count === 0 ? 'No arrivals in this window (or Beds24 is temporarily unavailable — refresh in a few minutes).' : 'No medium or high-risk arrivals in this window.'}</p>}
          {risky.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="risk-table">
                <thead><tr className="text-left text-slate-500"><th className="py-1">Risk</th><th>Guest</th><th>Arrival</th><th>Room</th><th>Source</th><th>Why</th><th className="text-right">Action</th></tr></thead>
                <tbody>
                  {risky.map((a) => (
                    <tr key={a.booking_id} className="border-t align-top" data-testid={`risk-row-${a.booking_id}`}>
                      <td className="py-1.5"><RiskBadge r={a} /></td>
                      <td className="font-medium">{a.guest_name || 'Guest'}{a.country ? <span className="text-slate-400 font-normal"> · {a.country}</span> : null}</td>
                      <td>{a.check_in} → {a.check_out}</td><td>{a.room_name}</td><td>{a.source}</td>
                      <td className="text-slate-600 max-w-[260px]">{a.factors.map((f) => FACTOR[f] || f).join(', ')}</td>
                      <td className="text-right whitespace-nowrap">
                        {a.reconfirm?.confirmed_at ? <span className="text-emerald-700 font-semibold">✓ confirmed</span>
                          : a.reconfirm?.sent_at ? <span className="text-slate-500">asked {a.reconfirm.sent_at.slice(0, 10)}</span>
                            : canEdit && a.email ? <button type="button" onClick={() => reconfirm(a)} className="inline-flex items-center gap-1 border rounded px-2 py-1 hover:bg-slate-50" data-testid={`risk-reconfirm-${a.booking_id}`}><Mail className="w-3.5 h-3.5" /> Re-confirm by e-mail</button>
                              : canEdit && a.phone && data.whatsapp_ready ? <button type="button" onClick={() => reconfirm(a)} className="inline-flex items-center gap-1 border border-emerald-300 text-emerald-800 rounded px-2 py-1 hover:bg-emerald-50" data-testid={`risk-reconfirm-wa-${a.booking_id}`}><Phone className="w-3.5 h-3.5" /> Re-confirm by WhatsApp</button>
                              : a.phone ? <a href={`https://wa.me/${String(a.phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700" data-testid={`risk-call-${a.booking_id}`}><Phone className="w-3.5 h-3.5" /> WhatsApp</a>
                                : <span className="text-slate-400">no contact</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskBoard;
